/**
 * Determines whether a boolean environment variable is enabled in the
 * environment.  An environment variable is considered "enabled" if its value is
 * the string 'true' (case-sensitive).  It also handles cases where `process` or
 * `process.env` might not be defined (e.g., in a non-Node.js environment).
 *
 * @param variable The name of the environment variable to check (e.g., *
 * 'MY_FEATURE_TOGGLE').
 * @returns True if the environment variable exists and its value is 'true',
 * otherwise false.
 */
export function isBooleanEnvEnabled(variableName: string): boolean {
  if (typeof process === 'undefined' || typeof process.env === 'undefined') {
    return false;
  }
  const value = process.env[variableName]?.toLowerCase();
  return value === 'true';
}

/**
 * Determines whether link prefetching is enabled in this environment.
 *
 * @returns True if link prefetching has been enabled.
 */
export function isLinkPrefetchingEnabled(): boolean {
  return isBooleanEnvEnabled('NEXT_PUBLIC_ENABLE_LINK_PREFETCH');
}

/**
 * @return True if the mock API has been enabled.
 */
export function haveMockApiEnabled(): boolean {
  return isBooleanEnvEnabled('NEXT_PUBLIC_USE_MOCK_APIGEE_API');
}

/**
 * @return True if slugs are enabled.
 */
export function isSlugEnabled() {
  return isBooleanEnvEnabled('NEXT_PUBLIC_FEATURE_FLAG_SLUG_ENABLE');
}

/* this some how does not work on runtime. Probably because isBooleanEnvEnabled('NEXT_PUBLIC_SSR_ENABLED') gets evaluated when env var is not available */
// export function isSsrEnabled(): boolean {
//   return isBooleanEnvEnabled('NEXT_PUBLIC_SSR_ENABLED');
// }

/**
 * @return True if SSR is enabled.
 */
export function isSsrEnabled(): boolean {
  return process.env.NEXT_PUBLIC_SSR_ENABLED?.toLowerCase() === 'true';
}
